import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { motion } from "framer-motion";
import { BarChart3 } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { CompanyDetail as CompanyDetailType } from "@/api/types";
import { cn } from "@/lib/utils";

type CellFormat = "currency_eur" | "percent" | "ratio";

interface DataRow {
  type?: undefined;
  label: string;
  /** Positional [N, N-1, N-2]. */
  values: [unknown, unknown, unknown];
  format: CellFormat;
  /** When true, the cell is clickable and edits write back via
   *  onUpdate(metadata_key, value). Required when editable. */
  editable: boolean;
  keys?: [string, string, string];
  /** Hint to the table row renderer: when true, a trend arrow
   *  (N vs N-1 % delta) is rendered next to the N cell. Only
   *  meaningful for currency rows where a positive delta is
   *  unambiguously good (revenue / EBITDA / net result). */
  showTrend?: boolean;
  /** Optional override: when the metric is "lower is better"
   *  (gross debt), flip the trend colour. */
  trendInverse?: boolean;
}

interface SeparatorRow {
  type: "separator";
}

type Row = DataRow | SeparatorRow;

interface FinancialsTableCardProps {
  company: CompanyDetailType;
  /** Persists a single metadata field. The parent's patchMetadata
   *  helper takes the same shape and chains rescore for
   *  SCORE_IMPACTING_FIELDS, so this stays a thin wrapper. */
  onUpdate: (key: string, value: number | null) => void;
}

/**
 * 3-year financial table + KPI cards + Revenue/EBITDA bar chart +
 * Margin/Leverage line chart, used in place of the dynamic field
 * grid for M&A projects. Data lives on `company.metadata` keyed by
 * the M&A engine's revenue_euros / revenue_n1_euros / revenue_n2_euros
 * naming. Computed fields (margins, leverage, CAGR, averages) are
 * read-only and only render when the engine has filled them in.
 */
export function FinancialsTableCard({
  company,
  onUpdate,
}: FinancialsTableCardProps) {
  const meta = (company.metadata ?? {}) as Record<string, unknown>;

  // French SMB accounts publish ~6 months after year-end, so the
  // most recent enriched year is "last calendar year" for most
  // dossiers. Tweak per project later if the engine starts dating
  // the snapshot itself.
  const currentYear = new Date().getFullYear() - 1;
  const years = useMemo(
    () => [
      { key: "N" as const, year: currentYear, suffix: "(N)" },
      { key: "N-1" as const, year: currentYear - 1, suffix: "(N-1)" },
      { key: "N-2" as const, year: currentYear - 2, suffix: "(N-2)" },
    ],
    [currentYear],
  );

  const rows: Row[] = [
    {
      label: "Revenue (CA)",
      values: [
        meta.revenue_euros,
        meta.revenue_n1_euros,
        meta.revenue_n2_euros,
      ],
      format: "currency_eur",
      editable: true,
      keys: ["revenue_euros", "revenue_n1_euros", "revenue_n2_euros"],
      showTrend: true,
    },
    {
      label: "EBITDA",
      values: [
        meta.ebitda_euros,
        meta.ebitda_n1_euros,
        meta.ebitda_n2_euros,
      ],
      format: "currency_eur",
      editable: true,
      keys: ["ebitda_euros", "ebitda_n1_euros", "ebitda_n2_euros"],
      showTrend: true,
    },
    {
      label: "Net Result",
      values: [
        meta.net_result_euros,
        meta.net_result_n1_euros,
        meta.net_result_n2_euros,
      ],
      format: "currency_eur",
      editable: true,
      keys: ["net_result_euros", "net_result_n1_euros", "net_result_n2_euros"],
      showTrend: true,
    },
    {
      label: "Gross Debt",
      values: [
        meta.gross_debt_euros,
        meta.gross_debt_n1_euros,
        meta.gross_debt_n2_euros,
      ],
      format: "currency_eur",
      editable: true,
      keys: ["gross_debt_euros", "gross_debt_n1_euros", "gross_debt_n2_euros"],
      showTrend: true,
      trendInverse: true,
    },
    { type: "separator" },
    {
      label: "EBITDA Margin",
      values: [
        meta.ebitda_margin_percent,
        meta.ebitda_margin_n1_percent,
        meta.ebitda_margin_n2_percent,
      ],
      format: "percent",
      editable: false,
    },
    {
      label: "Debt Leverage",
      // Prefer engine-shipped per-year ratios when present; fall
      // back to `gross_debt / ebitda` per year so the table stays
      // in sync with the line chart (which already backfills).
      values: [
        numOrNull(meta.debt_leverage_ratio) ??
          computeLeverage(meta.gross_debt_euros, meta.ebitda_euros),
        numOrNull(meta.debt_leverage_n1_ratio) ??
          computeLeverage(meta.gross_debt_n1_euros, meta.ebitda_n1_euros),
        numOrNull(meta.debt_leverage_n2_ratio) ??
          computeLeverage(meta.gross_debt_n2_euros, meta.ebitda_n2_euros),
      ],
      format: "ratio",
      editable: false,
    },
  ];

  // Empty-state gate: nothing to show if revenue and EBITDA are
  // both blank for every year. Surface a hint to run enrichment
  // rather than a half-empty chart.
  const hasAnyData = useMemo(() => {
    const candidates: unknown[] = [
      meta.revenue_euros,
      meta.revenue_n1_euros,
      meta.revenue_n2_euros,
      meta.ebitda_euros,
      meta.ebitda_n1_euros,
      meta.ebitda_n2_euros,
    ];
    return candidates.some((v) => Number.isFinite(toNumber(v)));
  }, [meta]);

  if (!hasAnyData) {
    return (
      <div className="py-8 text-center">
        <BarChart3 className="mx-auto mb-2 h-8 w-8 text-slate-200" />
        <p className="text-sm text-slate-400">No financial data available</p>
        <p className="mt-1 text-xs text-slate-300">
          Run Pappers enrichment to populate
        </p>
      </div>
    );
  }

  // KPI cards.
  const revenueCagr = toNumber(meta.revenue_cagr_percent);
  const avgMargin = toNumber(meta.average_ebitda_margin_percent);
  const debtN = toNumber(meta.gross_debt_euros);
  const debtN2 = toNumber(meta.gross_debt_n2_euros);
  const debtDeltaEur =
    Number.isFinite(debtN) && Number.isFinite(debtN2)
      ? debtN - debtN2
      : NaN;

  // Bar chart series (years as actual labels, not N / N-1 / N-2).
  // Most recent on the left so the chart axis matches the table
  // column order (N | N-1 | N-2).
  const barData = [
    {
      year: String(years[0].year),
      revenue: toNumber(meta.revenue_euros),
      ebitda: toNumber(meta.ebitda_euros),
    },
    {
      year: String(years[1].year),
      revenue: toNumber(meta.revenue_n1_euros),
      ebitda: toNumber(meta.ebitda_n1_euros),
    },
    {
      year: String(years[2].year),
      revenue: toNumber(meta.revenue_n2_euros),
      ebitda: toNumber(meta.ebitda_n2_euros),
    },
  ];
  const hasBarData = barData.some(
    (d) => Number.isFinite(d.revenue) || Number.isFinite(d.ebitda),
  );

  // Line chart series — same N → N-2 ordering as the bar chart and
  // the table. Margin keeps a 3-point series; leverage is backfilled
  // from `gross_debt / ebitda` per year when the engine didn't ship
  // a precomputed ratio (it currently only stores the N value).
  // Missing values stay `null` — Recharts' `filterNull` hides them
  // from the tooltip and `connectNulls` bridges gaps.
  const lineData = [
    {
      year: String(years[0].year),
      margin: numOrNull(meta.ebitda_margin_percent),
      leverage:
        numOrNull(meta.debt_leverage_ratio) ??
        computeLeverage(meta.gross_debt_euros, meta.ebitda_euros),
    },
    {
      year: String(years[1].year),
      margin: numOrNull(meta.ebitda_margin_n1_percent),
      leverage:
        numOrNull(meta.debt_leverage_n1_ratio) ??
        computeLeverage(meta.gross_debt_n1_euros, meta.ebitda_n1_euros),
    },
    {
      year: String(years[2].year),
      margin: numOrNull(meta.ebitda_margin_n2_percent),
      leverage:
        numOrNull(meta.debt_leverage_n2_ratio) ??
        computeLeverage(meta.gross_debt_n2_euros, meta.ebitda_n2_euros),
    },
  ];
  const hasLineData = lineData.some(
    (d) => d.margin !== null || d.leverage !== null,
  );

  return (
    <div className="space-y-5">
      {/* ─── Table ─────────────────────────────────────────── */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-900/[0.06]">
              <th className="w-[40%] py-2.5 text-left text-xs font-medium text-slate-400" />
              {years.map((y) => (
                <th
                  key={y.key}
                  className="w-[20%] py-2.5 text-right text-xs font-medium"
                >
                  <span className="text-slate-700">{y.year}</span>
                  <span className="ml-1 text-slate-400">{y.suffix}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              if (row.type === "separator") {
                return (
                  <tr key={`sep-${idx}`}>
                    <td colSpan={4} className="py-1">
                      <div className="border-t border-slate-900/[0.06]" />
                    </td>
                  </tr>
                );
              }
              return (
                <motion.tr
                  key={row.label}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05, duration: 0.2 }}
                  className="group border-b border-slate-900/[0.06] transition-colors duration-150 hover:bg-indigo-500/[0.04]"
                >
                  <td
                    className={cn(
                      "py-2",
                      row.editable
                        ? "font-medium text-slate-600"
                        : "text-xs text-slate-500",
                    )}
                  >
                    {row.label}
                  </td>
                  {row.values.map((val, i) => (
                    <td
                      key={i}
                      className={cn(
                        "py-2 text-right font-mono",
                        i === 0
                          ? "font-medium text-slate-800"
                          : i === 1
                            ? "text-slate-600"
                            : "text-slate-500",
                      )}
                    >
                      {row.editable && row.keys ? (
                        <span className="inline-flex items-center justify-end gap-1.5">
                          <EditableCell
                            value={val}
                            format={row.format}
                            onSave={(next) => onUpdate(row.keys![i], next)}
                          />
                          {i === 0 && row.showTrend && (
                            <TrendBadge
                              current={val}
                              previous={row.values[1]}
                              inverse={!!row.trendInverse}
                            />
                          )}
                        </span>
                      ) : (
                        <span>{formatValue(val, row.format)}</span>
                      )}
                    </td>
                  ))}
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ─── KPI cards ────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-3">
        <KpiMiniCard
          delay={0.3}
          label="Revenue CAGR"
          subtitle="3-year"
          tone={
            !Number.isFinite(revenueCagr)
              ? "neutral"
              : revenueCagr > 0
                ? "positive"
                : "negative"
          }
        >
          {Number.isFinite(revenueCagr)
            ? `${revenueCagr > 0 ? "+" : ""}${revenueCagr.toFixed(1)}%`
            : "—"}
        </KpiMiniCard>
        <KpiMiniCard
          delay={0.4}
          label="Avg EBITDA Margin"
          subtitle="3-year average"
          tone="neutral"
        >
          {Number.isFinite(avgMargin) ? `${avgMargin.toFixed(1)}%` : "—"}
        </KpiMiniCard>
        <KpiMiniCard
          delay={0.5}
          label="Debt Trend"
          subtitle="3-year delta"
          tone={
            !Number.isFinite(debtDeltaEur)
              ? "neutral"
              : debtDeltaEur < 0
                ? "positive"
                : "negative"
          }
        >
          {Number.isFinite(debtDeltaEur)
            ? formatSignedEur(debtDeltaEur)
            : "—"}
        </KpiMiniCard>
      </div>

      {/* ─── Bar chart: Revenue & EBITDA ─────────────────── */}
      {hasBarData && (
        <ChartFrame
          title="Revenue & EBITDA"
          legend={[
            { color: "#6366f1", label: "Revenue" },
            { color: "#10b981", label: "EBITDA" },
          ]}
        >
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={barData} barGap={4} barSize={20}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f3f4f6"
                vertical={false}
              />
              <XAxis
                dataKey="year"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "#9ca3af" }}
                tickFormatter={(v: number) => formatAxisEur(v)}
                axisLine={false}
                tickLine={false}
                width={45}
              />
              <Tooltip
                formatter={(value: number, name: string) => [
                  formatTooltipEur(value),
                  name === "revenue" ? "Revenue" : "EBITDA",
                ]}
                contentStyle={{
                  fontSize: 12,
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                }}
              />
              <Bar dataKey="revenue" fill="#6366f1" radius={[4, 4, 0, 0]} />
              <Bar dataKey="ebitda" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartFrame>
      )}

      {/* ─── Line chart: Margin (left) + Leverage (right) ─── */}
      {hasLineData && (
        <ChartFrame
          title="Profitability Trend"
          legend={[
            { color: "#f59e0b", label: "EBITDA Margin", solid: true },
            { color: "#fb7185", label: "Leverage", solid: false },
          ]}
        >
          <ResponsiveContainer width="100%" height={144}>
            <LineChart data={lineData}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f3f4f6"
                vertical={false}
              />
              <XAxis
                dataKey="year"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="margin"
                tick={{ fontSize: 10, fill: "#9ca3af" }}
                tickFormatter={(v: number) => `${v}%`}
                axisLine={false}
                tickLine={false}
                width={40}
                domain={[0, "auto"]}
              />
              <YAxis
                yAxisId="leverage"
                orientation="right"
                tick={{ fontSize: 10, fill: "#9ca3af" }}
                tickFormatter={(v: number) => `${v}x`}
                axisLine={false}
                tickLine={false}
                width={35}
                domain={[0, "auto"]}
              />
              <Tooltip
                formatter={(value, name) => {
                  // Bail on null / undefined / NaN so a year with no
                  // leverage (engine ships only N) doesn't render
                  // "NaNx" in the tooltip. Returning [null, null]
                  // makes Recharts drop the row.
                  if (
                    value == null ||
                    typeof value !== "number" ||
                    !Number.isFinite(value)
                  ) {
                    return [null, null] as [null, null];
                  }
                  return [
                    name === "margin"
                      ? `${value.toFixed(1)}%`
                      : `${value.toFixed(2)}x`,
                    name === "margin" ? "EBITDA Margin" : "Leverage",
                  ];
                }}
                contentStyle={{
                  fontSize: 12,
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                }}
              />
              <Line
                yAxisId="margin"
                type="monotone"
                dataKey="margin"
                stroke="#f59e0b"
                strokeWidth={2.5}
                dot={{ r: 4, fill: "#f59e0b" }}
                connectNulls
              />
              <Line
                yAxisId="leverage"
                type="monotone"
                dataKey="leverage"
                stroke="#fb7185"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ r: 3, fill: "#fb7185" }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartFrame>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────

interface TrendBadgeProps {
  current: unknown;
  previous: unknown;
  /** When the underlying metric is "lower is better" (gross debt). */
  inverse: boolean;
}

function TrendBadge({ current, previous, inverse }: TrendBadgeProps) {
  const cur = toNumber(current);
  const prev = toNumber(previous);
  if (!Number.isFinite(cur) || !Number.isFinite(prev) || prev === 0) {
    return null;
  }
  const pct = ((cur - prev) / Math.abs(prev)) * 100;
  if (!Number.isFinite(pct)) return null;
  const isUp = pct > 0;
  // For inverse metrics (debt), an increase is bad → recolour rose.
  const isPositive = inverse ? !isUp : isUp;
  return (
    <span
      className={cn(
        "text-[10px] font-medium tabular-nums",
        isPositive ? "text-emerald-500" : "text-rose-500",
      )}
    >
      {isUp ? "↑" : "↓"}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

interface KpiMiniCardProps {
  label: string;
  subtitle: string;
  tone: "positive" | "negative" | "neutral";
  delay?: number;
  children: React.ReactNode;
}

function KpiMiniCard({
  label,
  subtitle,
  tone,
  delay = 0,
  children,
}: KpiMiniCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.25 }}
      className="rounded-xl border border-slate-900/[0.06] bg-slate-50/80 px-3 py-2.5 text-center"
    >
      <div
        className={cn(
          "text-lg font-bold tabular-nums",
          tone === "positive" && "text-emerald-600",
          tone === "negative" && "text-rose-600",
          tone === "neutral" && "text-slate-800",
        )}
      >
        {children}
      </div>
      <div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">
        {label}
      </div>
      <div className="text-[9px] text-slate-300">{subtitle}</div>
    </motion.div>
  );
}

interface LegendEntry {
  color: string;
  label: string;
  /** Solid swatch (true) or dashed-line swatch (false) — line
   *  chart uses both to mirror the stroke style of the series. */
  solid?: boolean;
}

function ChartFrame({
  title,
  legend,
  children,
}: {
  title: string;
  legend: LegendEntry[];
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500">{title}</span>
        <div className="flex items-center gap-3 text-[10px] text-slate-400">
          {legend.map((l) => (
            <span key={l.label} className="flex items-center gap-1">
              {l.solid === false ? (
                <span
                  className="block h-0.5 w-3 rounded"
                  style={{ background: l.color }}
                />
              ) : (
                <span
                  className="block h-2 w-2 rounded-sm"
                  style={{ background: l.color }}
                />
              )}
              {l.label}
            </span>
          ))}
        </div>
      </div>
      {children}
    </div>
  );
}

interface EditableCellProps {
  value: unknown;
  format: CellFormat;
  onSave: (next: number | null) => void;
}

function EditableCell({ value, format, onSave }: EditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(stringify(value));

  // Re-seed the draft when the underlying value changes (e.g.
  // after a save settles or a saved view replays).
  useEffect(() => {
    if (!editing) setDraft(stringify(value));
  }, [value, editing]);

  function commit() {
    const trimmed = draft.trim();
    if (trimmed === "") {
      onSave(null);
    } else {
      const n = Number(trimmed);
      if (Number.isFinite(n)) onSave(n);
    }
    setEditing(false);
  }

  function cancel() {
    setDraft(stringify(value));
    setEditing(false);
  }

  function handleKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  }

  if (editing) {
    return (
      <input
        type="number"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        autoFocus
        className="h-6 w-full rounded border border-slate-200 px-1 text-right font-mono text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={() => setEditing(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setEditing(true);
        }
      }}
      className="-mx-1 cursor-text rounded px-1 transition-colors hover:bg-indigo-50"
    >
      {formatValue(value, format)}
    </span>
  );
}

// ─── Helpers ────────────────────────────────────────────────────

function stringify(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  return "";
}

function toNumber(value: unknown): number {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
}

/** `toNumber` but returns `null` when the value isn't a finite
 *  number. The line chart relies on `null` (not NaN) so Recharts'
 *  `filterNull` hides missing points from the tooltip. */
function numOrNull(value: unknown): number | null {
  const n = toNumber(value);
  return Number.isFinite(n) ? n : null;
}

/** Backfills leverage as `gross_debt / ebitda` for years where the
 *  engine doesn't ship a precomputed ratio. Returns null when either
 *  side is missing or EBITDA is zero (would yield Infinity). */
function computeLeverage(debt: unknown, ebitda: unknown): number | null {
  const d = toNumber(debt);
  const e = toNumber(ebitda);
  if (!Number.isFinite(d) || !Number.isFinite(e) || e === 0) return null;
  return Number((d / e).toFixed(2));
}

function formatValue(value: unknown, format: CellFormat): string {
  if (value == null || value === "") return "—";
  const n = toNumber(value);
  if (!Number.isFinite(n)) return "—";
  switch (format) {
    case "currency_eur":
      if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M €`;
      if (Math.abs(n) >= 1_000) return `${Math.round(n / 1_000)}K €`;
      return `${Math.round(n)} €`;
    case "percent":
      return `${n.toFixed(1)}%`;
    case "ratio":
      return `${n.toFixed(2)}x`;
  }
}

function formatAxisEur(v: number): string {
  if (!Number.isFinite(v)) return "";
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(0)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return String(v);
}

function formatTooltipEur(v: number): string {
  if (!Number.isFinite(v)) return "—";
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M €`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}K €`;
  return `${Math.round(v)} €`;
}

function formatSignedEur(v: number): string {
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M €`;
  if (abs >= 1_000) return `${sign}${Math.round(abs / 1_000)}K €`;
  return `${sign}${Math.round(abs)} €`;
}
